import {Box, ListItemButton, ListItemProps, SvgIcon, SxProps, Theme, Tooltip, Typography} from '@mui/material';
import {useLocation} from '@tanstack/react-router';
import {useEffect, useState} from 'react';

import {Link} from '~components/common/Link';
import {theme} from 'src/constants/theme';
import {textShortener} from '../../utils/miscUtils';

interface MainMenuItemProps extends ListItemProps {
  icon: any;
  text: string;
  linkTo: string;
  /**
   * Adds active styles when the pathname includes at least one of the given values.
   * This does not override the basic check if the first path segment matches the linkTo path.
   */
  onClick: () => void;
  activePaths?: string[];
  sx?: SxProps<Theme>;
  size?: 'small' | 'medium';
  expanded?: boolean;
  isEmpty?: boolean;
}

export function MainMenuItem({
  icon,
  size = 'medium',
  text,
  linkTo,
  activePaths,
  onClick,
  sx = [],
  expanded = false,
  isEmpty = false,
}: MainMenuItemProps) {
  const location = useLocation();
  const [isActive, setActive] = useState(false);
  const isSmall = size === 'small';
  const iconSize = isSmall ? 60 : 69;
  const viewBox = isSmall ? '0 0 60 60' : '0 0 60 69';

  useEffect(() => {
    setActive(() => {
      if (activePaths && activePaths.some((pathname) => location.pathname.includes(pathname))) {
        return true;
      } else {
        return location.pathname.startsWith('/' + linkTo.split('/')[1].split('?')[0]);
      }
    });
  }, [location.pathname, linkTo, activePaths]);

  const getIconColor = () => {
    if (isActive) {
      return theme.palette.maddox.accent;
    }
    if (isEmpty) {
      return '#8A6FD6';
    }
    return theme.palette.maddox.globalwhite;
  };

  const getBorderColor = () => {
    if (isActive) {
      return theme.palette.maddox.accent;
    }
    return theme.palette.secondary.dark;
  };

  return (
    <Tooltip title={!expanded ? text : ''} arrow placement='right'>
      <ListItemButton
        id={`main-menu-${linkTo.split('/')[1].split('?')[0]}`}
        data-testid={`main-menu-${text}`}
        component='li'
        sx={[
          {
            padding: 0,
            flexGrow: 0,
            '&:is(:hover, :focus, :active, &.Mui-focusVisible)': {
              background: 'none',
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        tabIndex={-1}
        onClick={onClick}
      >
        <Link
          href={linkTo}
          to={linkTo as any}
          className={isActive ? 'active' : ''}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            textDecoration: 'none',
            color: 'maddox.globalwhite',
            '&:hover, &:focus, &.active, :focus-visible': {
              outline: 0,
              color: 'maddox.accent',
            },

            '.main-menu-item-icon': {
              width: '60px',
              height: iconSize,
              '--border-color': getBorderColor(),
              '--icon-color': getIconColor(),
            },
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', marginLeft: 1, gap: 1}}>
            <SvgIcon className='main-menu-item-icon' component={icon} alt='' viewBox={viewBox} />
            {expanded && (
              <Typography
                variant='body1'
                sx={{
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  color: getIconColor(),
                  paddingTop: isSmall ? theme.spacing(1) : theme.spacing(2),
                }}
              >
                {textShortener(text, 9)}
              </Typography>
            )}
          </Box>
        </Link>
      </ListItemButton>
    </Tooltip>
  );
}
